import { apiBaseUrl } from './config.js';
import { writeCreds, type Credentials } from './creds.js';

interface AuthEnvelope {
  success?: boolean;
  data?: {
    user?: { email?: string };
    tokens?: { accessToken?: string };
    accessToken?: string;
  };
  error?: { message?: string };
}

function extractAccessToken(json: AuthEnvelope): string | undefined {
  return json.data?.tokens?.accessToken ?? json.data?.accessToken;
}

/** Pull the `fim_rt` refresh cookie out of a response's Set-Cookie headers. */
function extractRefreshCookie(res: Response): string | undefined {
  const getSetCookie = (res.headers as { getSetCookie?: () => string[] }).getSetCookie;
  const cookies = typeof getSetCookie === 'function' ? getSetCookie.call(res.headers) : [];
  for (const c of cookies) {
    if (c.startsWith('fim_rt=')) return c.split(';')[0];
  }
  return undefined;
}

/** Authenticate with email/password and persist credentials. */
export async function login(email: string, password: string): Promise<Credentials> {
  const apiUrl = apiBaseUrl();
  const res = await fetch(`${apiUrl}/api/v1/auth/login`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const json = (await res.json().catch(() => ({}))) as AuthEnvelope;
  const accessToken = extractAccessToken(json);
  if (!res.ok || json.success === false || !accessToken) {
    throw new Error(json.error?.message ?? `login failed (${res.status})`);
  }
  const creds: Credentials = {
    apiUrl,
    email: json.data?.user?.email ?? email,
    accessToken,
    refreshCookie: extractRefreshCookie(res),
  };
  writeCreds(creds);
  return creds;
}

/** Exchange the refresh cookie for a fresh access token (single-use rotation). */
export async function refresh(creds: Credentials): Promise<Credentials> {
  if (!creds.refreshCookie) throw new Error('not logged in — run `futurex login`');
  const res = await fetch(`${creds.apiUrl}/api/v1/auth/refresh`, {
    method: 'POST',
    headers: { cookie: creds.refreshCookie },
  });
  const json = (await res.json().catch(() => ({}))) as AuthEnvelope;
  const accessToken = extractAccessToken(json);
  if (!res.ok || !accessToken) {
    throw new Error('session expired — run `futurex login`');
  }
  const updated: Credentials = {
    ...creds,
    accessToken,
    refreshCookie: extractRefreshCookie(res) ?? creds.refreshCookie,
  };
  writeCreds(updated);
  return updated;
}
